import { groupsApis } from '@/configs/apis/groups';
import {
  GroupEncryptionStatus,
  GroupPosition,
  GroupSearchStatus,
} from '@/configs/apis/groups/interface';
import { getAvatar } from '@/scripts/utils/genAvatar';
import type {
  ChatChatId,
  ChatParams,
  CreateGroupChatParams,
} from '../hooks/useChats/types';
import { ChatCategory, InitType } from '../hooks/useChats/types';
import { serverTime } from '../hooks/useSystemTime/utils';
import type { ChatGroupMember } from '../hooks/useChatGroup/types';
import { ChatGroupMemberRemoved } from '../hooks/useChatGroup/types';
import type { ChatContextHooksFull, ChatContextHooksProps } from '../../context';

export type UseGroupChatsValueResult = ReturnType<typeof useGroupChatsValue>;

export function useGroupChatsValue({ hooks }: ChatContextHooksProps) {
  const initGroupChat = useCallback(
    async (params: ChatParams) => {
      const {
        chatsValue: { initChat },
        chatGroup,
      } = hooks as ChatContextHooksFull;
      return initChat(params, async (chatPartial) => {
        const { chat_id } = chatPartial;
        if (chat_id) {
          const group = await groupsApis.getGroupInfo(chat_id);
          const settings = await groupsApis.getGroupSettings(chat_id);
          if (group) {
            let members: ChatGroupMember[] = [];

            if (params.initType === InitType.CREATE) {
              if (params.users?.length) {
                members = params.users.map(
                  (user) =>
                    ({
                      user_id: user.user_id,
                      role: GroupPosition.Member,
                      avatar: getAvatar(user.avatar, user.name),
                      name: user.name,
                      username: user.username,
                      category: GroupPosition.Member,
                      create_at: user.create_at,
                      update_at: user.update_at,
                      remark_name: user.remark_name,
                      removed: ChatGroupMemberRemoved.Normal,
                    }) as ChatGroupMember
                );
              }
            } else {
              members = await groupsApis.getGroupMemberList(chat_id);
            }

            await Promise.all([
              chatGroup.insertGroups([
                {
                  ...group,
                  ...chatPartial,
                  group_id: chat_id,
                  is_encryption: group.is_encryption,
                },
              ]),
              chatGroup.insertSettings([
                {
                  group_id: chat_id,
                  group_name: group.group_name,
                  description: '',
                },
              ]),
              chatGroup.insertMembers(
                [
                  {
                    id: chat_id,
                    is_encryption: group.is_encryption,
                    args: members || [],
                  },
                ],
                !!(params.initType === InitType.CREATE && params.users?.length)
              ),
            ]);
            chatPartial.category = ChatCategory.GROUP;
            chatPartial.avatar = getAvatar(group.group_head, group.group_name);
            chatPartial.receiver_id = chat_id;
            chatPartial.name = group.group_name;
            chatPartial.create_at = group?.create_at || serverTime.time;
            chatPartial.update_at = chatPartial.create_at;
            chatPartial.expire_duration = settings.expire_duration;
          }
        }
      });
    },
    [hooks]
  );

  const createGroupChat = useCallback(
    async ({
      users,
      group_type,
      related_gid,
      is_encryption,
      ...other
    }: CreateGroupChatParams) => {
      const {
        chatsValue: { createChat },
        groupEncryption,
      } = hooks as ChatContextHooksFull;
      const res = await groupsApis.createGroup({
        search_status: GroupSearchStatus.Public,
        group_type,
        related_gid,
        invite_member_list:
          is_encryption === GroupEncryptionStatus.IS
            ? undefined
            : users?.map((user) => user.user_id),
        is_encryption,
        ...other,
      });

      const chat_id = res.gid;
      if (is_encryption === GroupEncryptionStatus.IS) {
        await groupEncryption.createGroupEncryption(chat_id);
      }

      return createChat(
        {
          chat_id,
          users,
          initType:
            is_encryption === GroupEncryptionStatus.IS ? InitType.CREATE : undefined,
          ...other,
        },
        initGroupChat
      );
    },
    [hooks, initGroupChat]
  );

  const loadGroupChatsByChatId = useCallback(
    async (params: ChatParams) => {
      const {
        chatsValue: { loadChatByChatId },
        groupEncryption,
        chatGroup,
      } = hooks as ChatContextHooksFull;
      return loadChatByChatId(params, initGroupChat, async (chatPartial) => {
        const groupInfo = await chatGroup.chatGroupDB.getGroups([chatPartial.chat_id!]);
        return groupInfo?.[0]?.is_encryption === GroupEncryptionStatus.IS
          ? groupEncryption.initLoadGroups([chatPartial.chat_id!])
          : Promise.resolve();
      });
    },
    [hooks, initGroupChat]
  );

  const syncGroupSettings = useCallback(
    async (chat_id: ChatChatId) => {
      const settings = await groupsApis.getGroupSettings(chat_id);
      if (settings?.expire_duration) {
        const {
          chats: { updateChats },
        } = hooks as ChatContextHooksFull;
        // TODO amo: There are other updates here
        await updateChats([
          {
            chat_id,
            expire_duration: settings.expire_duration,
          },
        ]);
      }
    },
    [hooks]
  );

  return useMemo(
    () => ({
      initGroupChat,
      createGroupChat,
      loadGroupChatsByChatId,
      syncGroupSettings,
    }),
    [createGroupChat, initGroupChat, loadGroupChatsByChatId, syncGroupSettings]
  );
}
